import PropTypes from 'prop-types';

import { Helmet } from 'core/libs/helmet';

import H4 from 'core/components/H4';
import Feed from 'core/components/Feed';
import PageLoader from 'core/components/Loader/PageLoader';

import getImageUrl from 'core/utils/smart-image';

import ListPage from 'core/components/ListPage';
import AuthorCard from './AuthorCard';

import styles from './index.styl';


/**
 * @author Aleksey Rezakov <a.rezakov@rambler-co.ru>
 */
function AuthorPage(props, context) {
  const {
    topics,
    meta,
    author,
    limit,
    card,
    authorCard: CardAuthor,
    columns,
    interitemSpacing,
    description,
    titleBuilder,
    descriptionBuilder,
    grid,
    cardsReplacement,
    pagination,
    feedRenderer: FeedRenderer,
    topicsTitleComponent: TopicsTitleComponent,
    hideTopicsTitle,
    allAuthorsLinkComponent,
    isLoading,
  } = props;

  if (!author) return null;

  const {
    mediaUrlPrefix,
    host,
    webdavHost,
  } = context;

  const allAuthorsLink = `${mediaUrlPrefix || ''}/authors`;

  const {
    name,
    slug,
    biography,
    avatar,
    is_organization: isOrganization,
  } = author.attributes;

  const FeedComponent = FeedRenderer || Feed;

  // Формируем микроразметку статьи
  const jsonLd = {
    '@context': 'http://schema.org',
    '@type': isOrganization ? 'Organization' : 'Person',
    'mainEntityOfPage': `https://${host}${mediaUrlPrefix ? '/' + mediaUrlPrefix : ''}/authors/${slug}`,
    'description': biography,
    'name': name,
  };

  if (avatar) {
    const imageUrl = getImageUrl({ src: avatar?.attributes?.versions?.original, webdavHost });
    jsonLd.image = `https://${host}${imageUrl}`;
  }

  return (
    <>
      <CardAuthor
        author={author}
        allAuthorsLink={allAuthorsLink}
        allAuthorsLinkComponent={allAuthorsLinkComponent}
      />
      <ListPage
        title={typeof titleBuilder === 'function' ? titleBuilder(author) : name}
        description={typeof descriptionBuilder === 'function' ? descriptionBuilder(author) : description}
        rawData={topics}
        meta={meta}
        limit={limit}
        pagination={pagination}
        isLoading={isLoading}
      >
        {({ content, count }) => isLoading
          ? <PageLoader />
          : (
            <>
              {count > 0 && (
                <div className={styles.topics}>
                  {!hideTopicsTitle &&
                    <TopicsTitleComponent>Материалы автора ({count})</TopicsTitleComponent>
                  }
                  <div className={styles.topicsList}>
                    <FeedComponent
                      content={content}
                      card={card}
                      columns={columns}
                      limit={limit}
                      interitemSpacing={interitemSpacing}
                      grid={grid}
                      cardsReplacement={cardsReplacement}
                      dataQa='feed'
                    />
                  </div>
                </div>
              )}
              <Helmet>
                <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
              </Helmet>
            </>
          )}
      </ListPage>
    </>
  );
}

AuthorPage.defaultProps = {
  columns: 1,
  interitemSpacing: 20,
  limit: 10,
  authorCard: AuthorCard,
  topicsTitleComponent: H4,
  hideTopicsTitle: false,
};

AuthorPage.contextTypes = {
  mediaUrlPrefix: PropTypes.string,
  host: PropTypes.string,
  webdavHost: PropTypes.string,
};


AuthorPage.propTypes = {
  /**
   * Сырые НЕденормализованые данные от апишки.<br/>
   * Пробрасываются в `<ListPage />`
   */
  topics: PropTypes.shape({
    data: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired,
    included: PropTypes.array,
  }),
  /**
   * Данные об авторе
   */
  author: PropTypes.object,
  /**
   * Мета-данные для пагинации ListPage
   */
  meta: PropTypes.shape({
    filtered_count: PropTypes.number,
    search_result: PropTypes.shape({
      total: PropTypes.number,
    }),
  }),
  limit: PropTypes.number,
  /**
   * Карточка для отображения в Feed.
   */
  card: PropTypes.func,
  /**
   * Карточка автора
   */
  authorCard: PropTypes.func.isRequired,
  columns: PropTypes.number,
  interitemSpacing: PropTypes.number,
  /**
   * description может быть передан в ListPage.<br/>
   * При этом приоритет у descriptionBuilder выше.
   */
  description: PropTypes.string,
  /**
   * Функция-билдер мета-тайтла для страницы
   */
  titleBuilder: PropTypes.func,
  /**
   * Функция-билдер мета-дескрипшена для страницы
   */
  descriptionBuilder: PropTypes.func,
  grid: PropTypes.bool,
  cardsReplacement: PropTypes.object,
  /**
   * Компонент или тег для содержимого ссылки на страницу "Все авторы"
   */
  allAuthorsLinkComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * Компонент кастомной паджинации
   */
  pagination: PropTypes.func,
  /**
   * Компонент, который будет рендерить карточки (Feed, Drum, etc.)
   */
  feedRenderer: PropTypes.func,
  /**
   * Компонент или тег для заголовка "Материалы автора".
   */
  topicsTitleComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * Флаг, позволяющий не отображать заголовок "Материалы автора"
   */
  hideTopicsTitle: PropTypes.bool,

  /** Признак загрузки топиков для передачи в ListPage */
  isLoading: PropTypes.bool,
};

export { AuthorPage as StorybookComponent };
export default AuthorPage;
